// Génération de l'aperçu en langage naturel pour fonctions, automatisations, événements.
// Utilisé à la fois par les pages de création et les listings.

const JOUR_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export function buildFonctionPreview(fonction) {
  if (!fonction) return null;
  const capteurs = fonction.capteurs || [];
  const actionneurs = fonction.actionneurs || [];
  if (capteurs.length === 0) return null;

  const capteurParts = capteurs.map(c => {
    const analyseLC = (c.analyse || "Moyenne").toLowerCase();
    const periode = c.periode || "1d";
    return `${c.type || ""} ${c.id || ""} a fait une ${analyseLC} ${c.condition} ${c.valeur} pendant ${periode}`.trim();
  });
  const conditionText = capteurParts.length > 1 ? capteurParts.join(" ET ") : capteurParts[0];

  const actionParts = actionneurs.map(a =>
    `${a.action === "Désactiver" ? "Désactiver" : "Activer"} (${a.type || ""} ${a.id || ""})`.trim()
  );
  const actionText = actionParts.length > 0 ? actionParts.join(" et ") : "aucune action définie";

  return { conditionText, actionText, sensorCount: capteurs.length, actionCount: actionneurs.length };
}

export function buildAutomatisationPreview(auto) {
  if (!auto) return null;
  const jours = auto.selectedJours || auto.jours || [];
  const mois = auto.selectedMois || auto.mois || [];
  const creneaux = auto.creneaux || [];
  const actionneurs = auto.actionneurs || [];
  if (creneaux.length === 0 || jours.length === 0 || actionneurs.length === 0) return null;

  const joursText = [...jours].sort((a, b) => a - b).map(i => JOUR_LABELS[i]).filter(Boolean).join(", ");
  const moisText = mois.length > 0 && mois.length < 12 ? mois.join(", ") : null;

  const actName = (id) => {
    const a = actionneurs.find(x => (x._id || x) === id || x === id);
    if (!a) return typeof id === "string" ? id.slice(-6) : id;
    return `${a.type || ""} ${a.sn || a._id || ""}`.trim();
  };

  const toMin = (hhmm) => {
    const [h, m] = (hhmm || '0:0').split(':').map(Number);
    return h * 60 + m;
  };
  const creneauxText = creneaux.map(c => {
    const verb = c.action === "Désactiver" ? "Désactiver" : "Activer";
    const ids = (c.actionneurs && c.actionneurs.length > 0)
      ? c.actionneurs
      : actionneurs.map(a => a._id || a);
    const cibles = ids.map(actName).join(" et ");
    const crosses = toMin(c.fin) <= toMin(c.debut);
    const finSuffix = crosses ? `${c.fin} le lendemain` : c.fin;
    return `${verb} ${cibles} de ${c.debut} à ${finSuffix}`;
  });

  return { joursText, moisText, creneauxText, jourCount: jours.length, actionCount: actionneurs.length, creneauCount: creneaux.length };
}

export function buildEvenementPreview(evt) {
  if (!evt) return null;
  const nodes = evt.nodes || [];
  const connections = evt.connections || [];
  if (nodes.length === 0) return null;

  const nodeMap = Object.fromEntries(nodes.map(n => [n.instanceId, n]));
  const sensorConditions = [];
  const actionneurActions = [];

  connections.forEach(conn => {
    const fromNode = nodeMap[conn.fromId];
    if (fromNode?.nodeType === "capteur" && conn.condition && conn.valeur != null && conn.valeur !== "") {
      sensorConditions.push(`${fromNode.type || fromNode.label} ${fromNode.id} ${conn.condition} ${conn.valeur}`);
    }
  });

  nodes.filter(n => n.nodeType === "actionneur").forEach(n => {
    const verb = (n.action || "Activer") === "Désactiver" ? "Désactiver" : "Activer";
    actionneurActions.push(`${verb} ${n.label} ${n.id}`);
  });

  const ops = nodes.filter(n => n.nodeType === "operateur").map(n => n.label);

  if (sensorConditions.length === 0 && actionneurActions.length === 0) return null;

  const conditionText = sensorConditions.length > 0
    ? (ops.length > 0 ? sensorConditions.join(` [${ops.join(", ")}] `) : sensorConditions.join(" ET "))
    : "(aucune condition définie)";
  const actionText = actionneurActions.length > 0
    ? actionneurActions.join(" et ")
    : "(aucune action définie)";

  return { conditionText, actionText, sensorCount: sensorConditions.length, actionCount: actionneurActions.length };
}
